import Vue from 'vue'
import {
  ValidationObserver,
  ValidationProvider,
  extend,
  localize,
} from 'vee-validate'
import de from 'vee-validate/dist/locale/de.json'
import * as rules from 'vee-validate/dist/rules'
import { parse, isBefore, isAfter, isValid, subDays } from 'date-fns'

import { removeApostroph } from '~/utils'

// install rules and localization
const registerValidationRules = () => {
  extend('required', rules.required)
  extend('min', rules.min)
  extend('max', rules.max)
  extend('email', rules.email)
  extend('max_value', rules.max_value)
  extend('formatted_min_value', {
    ...rules.min_value,
    validate(value, params) {
      const normalizedValue = Array.isArray(value)
        ? value.map((entry) => removeApostroph(entry))
        : removeApostroph(value)

      return rules.min_value.validate(normalizedValue, params)
    },
    message: de.messages.min_value,
  })
  extend('formatted_max_value', {
    ...rules.max_value,
    validate(value, params) {
      const normalizedValue = Array.isArray(value)
        ? value.map((entry) => removeApostroph(entry))
        : removeApostroph(value)

      return rules.max_value.validate(normalizedValue, params)
    },
    message: de.messages.max_value,
  })
  extend('numeric', {
    validate: (value) => {
      if (value === null || value === undefined || value === '') {
        return {
          valid: false,
        }
      }

      value = removeApostroph(value)

      value = Number(value)
      return {
        valid: !isNaN(value) && typeof value === 'number',
      }
    },
    message: '{_field_} darf nur numerische Zeichen enthalten',
  })
  extend('decimal', {
    validate: (value, params) => {
      const decimals = params[0] ? parseInt(params[0]) : '*'
      if (value === null || value === undefined || value === '') {
        return {
          valid: false,
        }
      }

      value = removeApostroph(value)
      if (decimals === 0) {
        return {
          valid: /^-?\d*$/.test(value),
        }
      }
      const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`
      const regex = new RegExp(
        `^[-+]?\\d*(\\.\\d${regexPart})?([eE]{1}[-]?\\d+)?$`
      )

      return {
        valid: regex.test(value),
      }
    },
    message: (field, params) => {
      const decimals = params[0] ? parseInt(params[0]) : null

      if (decimals === 0) {
        return `${field} muss eine Ganzzahl sein`
      }
      if (!decimals) {
        return `${field} muss eine Dezimalzahl sein`
      }

      return `${field} muss eine Dezimalzahl sein mit maximal ${decimals} Nachkommastellen`
    },
  })
  extend('date', {
    validate(value) {
      if (!value) {
        return true
      }

      const match = value.match(
        /([0][1-9]|[1-2][0-9]|3[0-1])\.(0[1-9]|1[0-2])\.([1-2][0-9]{3})/
      )
      if (!match) {
        return false
      }
      if (match[0] !== match.input?.trim()) {
        return false
      }
      const date = parse(value, 'dd.MM.yyyy', new Date())
      return isValid(date)
    },
    message: 'Datumformat ist nicht korrekt',
  })
  extend('before', {
    validate(value, { beforeDate }) {
      if (!value) {
        return true
      }

      if (!beforeDate || beforeDate === 'today') {
        beforeDate = new Date()
      } else if (beforeDate === 'tomorrow') {
        beforeDate = subDays(new Date(), -1)
      } else {
        beforeDate = parse(beforeDate, 'dd.MM.yyyy', new Date())
      }

      const date = parse(value, 'dd.MM.yyyy', new Date())

      return isBefore(date, beforeDate)
    },
    params: ['beforeDate'],
    message: (field, values) => {
      if (values) {
        if (!values.beforeDate || values.beforeDate === 'today') {
          return 'Datum muss in der Vergangenheit liegen'
        }
        return `Datum muss vor ${values.beforeDate} sein`
      }
      return ''
    },
  })
  extend('after', {
    validate(value, { afterDate }) {
      // Allow empty values
      if (!value) {
        return true
      }

      // Helper function to check if input is a year (4-digit number)
      const isYear = (input) => /^\d{4}$/.test(input)

      // Determine input type (year or date)
      let inputDate
      let inputYear
      if (isYear(value)) {
        inputYear = parseInt(value, 10)
        // Create a date for comparison (use Jan 1st of the year)
        inputDate = new Date(inputYear, 0, 1)
      } else {
        inputDate = parse(value, 'dd.MM.yyyy', new Date())
        if (isNaN(inputDate.getTime())) {
          return false // Invalid date format
        }
      }

      // Determine comparison date/year
      let comparisonDate
      let comparisonYear
      if (!afterDate || afterDate === 'today') {
        comparisonDate = new Date()
        comparisonYear = comparisonDate.getFullYear()
      } else if (afterDate === 'yesterday') {
        comparisonDate = subDays(new Date(), 1)
        comparisonYear = comparisonDate.getFullYear()
      } else if (isYear(afterDate)) {
        comparisonYear = parseInt(afterDate, 10)
        comparisonDate = new Date(comparisonYear, 0, 1)
      } else {
        comparisonDate = parse(afterDate, 'dd.MM.yyyy', new Date())
        if (isNaN(comparisonDate.getTime())) {
          return false // Invalid comparison date format
        }
        comparisonYear = comparisonDate.getFullYear()
      }

      // Perform validation
      if (inputYear && comparisonYear) {
        // Year-to-year comparison
        return inputYear > comparisonYear
      } else {
        // Date-to-date comparison
        return isAfter(inputDate, comparisonDate)
      }
    },
    params: ['afterDate'],
    message: (field, values) => {
      if (values) {
        if (
          !values.afterDate ||
          values.afterDate === 'today' ||
          values.afterDate === 'yesterday'
        ) {
          return 'Datum muss in der Zukunft liegen'
        }
        return `Datum muss nach ${values.afterDate} sein`
      }
      return ''
    },
  })
  extend('confirmValue', {
    validate: (value, params) => {
      if (value === null || value === undefined || value === '') {
        return {
          valid: false,
        }
      }

      const confirmation = params[0] ? params[0] : null

      return {
        valid: value === confirmation,
      }
    },
    message: `Falsche Bestätigung für {_field_} eingegeben`,
  })

  extend('min_max_sum', {
    params: ['otherValue'],
    validate(value, { otherValue }) {
      // Convert values to numbers and check the sum
      const comparisonValue = isNaN(parseFloat(otherValue))
        ? 0
        : parseFloat(otherValue)
      const currentValue = isNaN(parseFloat(value)) ? 0 : parseFloat(value)
      return currentValue + comparisonValue === 100
    },
    message: 'Beide Anteilsfelder müssen zusammen 100% ergeben',
  })

  extend('year', {
    validate(value) {
      // Validates that the input is a four-digit number between 1901 and 2155
      return (
        /^\d{4}$/.test(value) &&
        parseInt(value) >= 1901 &&
        parseInt(value) <= 2155
      )
    },
    message: '{_field_} muss ein gültiges Jahr zwischen 1901 und 2155 sein',
  })

  extend('min_value', {
    validate(value, { minValue }) {
      if (value === null || value === undefined || value === '') {
        return {
          valid: false,
        }
      }

      value = removeApostroph(value)

      value = Number(value)
      minValue = Number(minValue)
      return {
        valid: !isNaN(value) && typeof value === 'number' && value >= minValue,
      }
    },
    params: ['minValue'],
    message: '{_field_} muss mindestens {minValue} sein',
  })

  extend('phone', {
    validate(value) {
      if (value === null || value === undefined || value === '') {
        return true
      }

      let normalized = String(value)
        .trim()
        .replace(/[^\d+]/g, '')
      normalized = normalized.replace(/(?!^)\+/g, '')

      if (normalized.startsWith('00')) {
        normalized = `+${normalized.slice(2)}`
      }

      return /^(?:\+)?\d{9,15}$/.test(normalized)
    },
    message: '{_field_} muss eine gültige Telefonnummer sein',
  })
}

registerValidationRules()

localize('de', de)

// Install components globally
Vue.component('ValidationObserver', ValidationObserver)
Vue.component('ValidationProvider', ValidationProvider)

Vue.config.productionTip = false
